import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiFetch, apiFetchBlob } from '@/lib/api'
import { ApiError } from '@/lib/problem'
import { queryKeys } from '@/lib/query-keys'
import type { components } from '@/types/api'

/*
 * `/api/v1/bi/reports/{id}/runs*` (docs/REPORT-DOCUMENTS-PLAN.md §5): belge dönem kopyaları.
 * Veriyi sunucu hesaplamaz; `content`/`data`/`condition_results` tarayıcıda üretilip gönderilir.
 * Hatalar makinece okunur: `document_run_exists`, `run_not_draft`, `version_conflict`, 403, 413.
 */

export type DocumentRun = components['schemas']['RunRead']
export type DocumentRunSummary = components['schemas']['RunSummary']
export type RunCreateBody = components['schemas']['RunCreate']
export type RunRefreshBody = components['schemas']['RunRefresh']
export type RunRegenerateBody = components['schemas']['RunRegenerate']

const base = (id: string) => `/bi/reports/${id}/runs`
const one = (id: string, runId: string) => `${base(id)}/${runId}`

export const listRuns = (id: string) => apiFetch<DocumentRunSummary[]>(base(id))

export const getRun = (id: string, runId: string) => apiFetch<DocumentRun>(one(id, runId))

export const generateRun = (id: string, body: RunCreateBody) =>
  apiFetch<DocumentRunSummary>(base(id), { method: 'POST', body })

export const updateRunText = (
  id: string,
  runId: string,
  body: { content: object; expected_version: number },
) => apiFetch<DocumentRunSummary>(one(id, runId), { method: 'PUT', body })

export const refreshRun = (id: string, runId: string, body: RunRefreshBody) =>
  apiFetch<DocumentRunSummary>(`${one(id, runId)}/refresh`, { method: 'POST', body })

export const regenerateRun = (id: string, runId: string, body: RunRegenerateBody) =>
  apiFetch<DocumentRunSummary>(`${one(id, runId)}/regenerate`, { method: 'POST', body })

export const finalizeRun = (id: string, runId: string, expectedVersion: number) =>
  apiFetch<DocumentRunSummary>(`${one(id, runId)}/finalize`, {
    method: 'POST',
    body: { expected_version: expectedVersion },
  })

export const deleteRun = (id: string, runId: string) =>
  apiFetch<null>(one(id, runId), { method: 'DELETE' })

/** `Content-Disposition` dosya adı: RFC 5987 `filename*` önce, yoksa `filename`. */
export function dispositionFilename(header: string | null, fallback: string): string {
  const star = /filename\*\s*=\s*([^']*)'[^']*'([^;]+)/i.exec(header ?? '')
  if (star?.[2]) {
    try {
      return decodeURIComponent(star[2].trim())
    } catch {
      // bozuk yüzde kodu: düz `filename`'e düş
    }
  }
  const plain = /filename\s*=\s*(?:"([^"]*)"|([^;]+))/i.exec(header ?? '')
  return plain?.[1] ?? plain?.[2]?.trim() ?? fallback
}

/** Sunucu PDF'ini getirir; dosya adı sunucunun verdiği addır. Hata `ApiError` (`pdf_*` kodları). */
export async function fetchRunPdf(
  id: string,
  runId: string,
): Promise<{ blob: Blob; filename: string }> {
  const { blob, contentDisposition } = await apiFetchBlob(`${one(id, runId)}/pdf`, {
    accept: 'application/pdf',
  })
  return { blob, filename: dispositionFilename(contentDisposition, 'belge.pdf') }
}

/** 409 `document_run_exists`: dönemin kopyası var; hatanın taşıdığı mevcut kopya kimliği. */
export function existingRunId(error: unknown): string | undefined {
  if (!(error instanceof ApiError) || error.code !== 'document_run_exists') return undefined
  return error.fieldErrors.find((item) => item.field === 'existing_run_id')?.message
}

/** 409 `version_conflict`: kopyayı başkası kaydetti (`expected_version` uyuşmadı). */
export const isRunVersionConflict = (error: unknown) =>
  error instanceof ApiError && error.status === 409 && error.code === 'version_conflict'

export function useRuns(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.bi.runs(id ?? ''),
    queryFn: () => listRuns(id as string),
    enabled: id !== undefined,
  })
}

export function useRun(id: string | undefined, runId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.bi.run(id ?? '', runId ?? ''),
    queryFn: () => getRun(id as string, runId as string),
    enabled: id !== undefined && runId !== undefined,
  })
}

/** Yazma sonrası kopya ve liste yeniden çekilir (sürüm/durum değişti). */
function useRunMutation<Variables, Result>(
  id: string,
  mutationFn: (variables: Variables) => Promise<Result>,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.bi.runs(id) }),
  })
}

export const useGenerateRun = (id: string) =>
  useRunMutation(id, (body: RunCreateBody) => generateRun(id, body))

export const useUpdateRunText = (id: string, runId: string) =>
  useRunMutation(id, (body: Parameters<typeof updateRunText>[2]) => updateRunText(id, runId, body))

export const useRefreshRun = (id: string, runId: string) =>
  useRunMutation(id, (body: RunRefreshBody) => refreshRun(id, runId, body))

export const useRegenerateRun = (id: string, runId: string) =>
  useRunMutation(id, (body: RunRegenerateBody) => regenerateRun(id, runId, body))

export const useFinalizeRun = (id: string, runId: string) =>
  useRunMutation(id, (expectedVersion: number) => finalizeRun(id, runId, expectedVersion))

export const useDeleteRun = (id: string, runId: string) =>
  useRunMutation(id, () => deleteRun(id, runId))
